import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, user, loading: authLoading } = useAuth();
  
  // If user is already authenticated, redirect to home or dashboard
  if (isAuthenticated && !authLoading) {
    const redirectTo = user?.isPlatformAdmin ? '/dashboard' : '/businesses';
    return <Navigate to={redirectTo} replace />;
  }
  
  // Get redirect path from location state or default to home page
  const from = location.state?.from || '/';
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }
    
    try {
      setError('');
      setLoading(true);
      
      // Call login function from auth context
      const userData = await login(email, password);
      
      // For platform admins, clear any previously selected business
      if (userData && userData.is_platform_admin) {
        localStorage.removeItem('selectedBusinessId');
      }
      
      // Redirect to the page they were trying to access
      navigate(from, { replace: true });
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Failed to log in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="max-w-md w-full p-6 rounded-xl bg-dark-50 border border-dark-300 shadow-xl">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-white mb-2">Admin Portal</h1>
          <p className="text-gray-400">Sign in to manage your voice ordering system</p>
        </div>
        
        {error && (
          <div className="bg-red-900 bg-opacity-20 border border-red-800 text-red-400 px-4 py-3 rounded-md mb-6">
            <p>{error}</p>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="form-label">Email</label>
            <input
              id="email"
              type="email"
              className="w-full p-2 bg-dark-200 border border-dark-300 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              required
              autoFocus
              autoComplete="email"
            />
          </div>
          
          <div>
            <div className="flex justify-between">
              <label htmlFor="password" className="form-label">Password</label>
              <a href="#" className="text-sm text-primary-500 hover:text-primary-400">Forgot password?</a>
            </div>
            <input
              id="password"
              type="password"
              className="w-full p-2 bg-dark-200 border border-dark-300 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              required
              autoComplete="current-password"
            />
          </div>
          
          <div>
            <button
              type="submit"
              className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-700 text-white rounded-md font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-dark-900 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>
        </form>
        
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-400">
            Copyright © Voice Ordering System {new Date().getFullYear()}
          </p>
          <p className="text-sm text-gray-400">
            Admin Portal
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;